import {
  capacityDirectoryResponseSchema,
  latestCapacitySnapshotResponseSchema,
  listCapacitySpacesResponseSchema,
  listCapacityUsageRowsResponseSchema,
  startCapacityCollectionResponseSchema,
  type CapacityDirectoryResponse,
  type CapacityRowKind,
  type CapacityRowsSort,
  type CapacitySnapshot,
  type CapacityUsageRow,
  type ListCapacitySpacesResponse,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequestJson } from "../api-client";

const BASE = `${API_V1_PREFIX}/capacity`;

// Idempotent on the server: a collection already running for the connection
// comes back as `created: false` instead of enqueueing a second one.
export async function startCapacityCollection(
  connectionId: string,
): Promise<{ snapshot: CapacitySnapshot; created: boolean }> {
  const res = await apiRequestJson({
    path: `${BASE}/collect`,
    method: "POST",
    body: { connectionId },
    responseSchema: startCapacityCollectionResponseSchema,
    badResponseMessage: "Failed to start capacity collection",
  });
  return { snapshot: res.data, created: res.created };
}

// null when the connection was never collected — a first-run empty state, not
// an error.
export async function getLatestCapacitySnapshot(
  connectionId: string,
): Promise<CapacitySnapshot | null> {
  const res = await apiRequestJson({
    path: `${BASE}/snapshots/latest?connectionId=${encodeURIComponent(connectionId)}`,
    responseSchema: latestCapacitySnapshotResponseSchema,
    badResponseMessage: "Failed to load capacity snapshot",
  });
  return res.data;
}

// Consumption ranking of the snapshot's measured day, keyset-paginated. `kind`
// narrows it to one tab's ranking; omitted means all rows. search/space/sort run
// server-side over the whole ranking — the cursor encodes the active sort, so a
// filter or sort change must restart the paging instead of reusing the cursor.
export async function listCapacityUsageRows(
  snapshotId: string,
  params?: {
    limit?: number;
    cursor?: string;
    kind?: CapacityRowKind;
    sort?: CapacityRowsSort;
    search?: string;
    space?: string;
  },
): Promise<{ data: CapacityUsageRow[]; nextCursor?: string }> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.cursor) search.set("cursor", params.cursor);
  if (params?.kind) search.set("kind", params.kind);
  if (params?.sort) search.set("sort", params.sort);
  if (params?.search) search.set("search", params.search);
  if (params?.space) search.set("space", params.space);
  const qs = search.size > 0 ? `?${search.toString()}` : "";
  return apiRequestJson({
    path: `${BASE}/snapshots/${snapshotId}/rows${qs}`,
    responseSchema: listCapacityUsageRowsResponseSchema,
    badResponseMessage: "Failed to load capacity usage rows",
  });
}

// The spaces of one ranking's population: the space filter's options, and — for
// the datafile ranking — the first level of the per-space view. An app's space
// is where the app sits, a datafile's is where the file sits, so the two tabs
// must not share one list. `unassigned` carries the same population's rows whose
// space never resolved: no space to navigate into, but the per-space view still
// has to account for them.
export async function listCapacitySpaces(
  snapshotId: string,
  kind: CapacityRowKind,
): Promise<ListCapacitySpacesResponse> {
  return apiRequestJson({
    path: `${BASE}/snapshots/${snapshotId}/spaces?kind=${encodeURIComponent(kind)}`,
    responseSchema: listCapacitySpacesResponseSchema,
    badResponseMessage: "Failed to load capacity spaces",
  });
}

// One level of a space's directory tree, aggregated server-side. `prefix` is ""
// for the space root, otherwise a path ending in "/".
export async function listCapacityDirectory(
  snapshotId: string,
  params: { space: string; prefix?: string },
): Promise<CapacityDirectoryResponse> {
  const search = new URLSearchParams({ space: params.space });
  if (params.prefix) search.set("prefix", params.prefix);
  return apiRequestJson({
    path: `${BASE}/snapshots/${snapshotId}/directories?${search.toString()}`,
    responseSchema: capacityDirectoryResponseSchema,
    badResponseMessage: "Failed to load capacity directories",
  });
}
